import { useMemo } from 'react';
interface UseDialogSubmitStateOptions {
  /** Whether the mutation/action is currently running */
  isSubmitting: boolean;
  /** For edit dialogs: whether form values differ from initial values */
  isDirty?: boolean;
  /** Validation error message (empty string = valid) */
  validationError?: string;
  /** For create dialogs: whether required fields are filled */
  isRequiredFilled?: boolean;
  /** For confirm dialogs: whether user has confirmed (typed DELETE, checked box, etc.) */
  isConfirmed?: boolean;
}

interface DialogSubmitState {
  disabled: boolean;
  disabledTooltip: string;
}

/**
 * Duplicated from apps/web per spec §6.4 (rule of three).
 * Standardizes save/confirm button disable logic across all dialogs.
 */
export function useDialogSubmitState(options: UseDialogSubmitStateOptions): DialogSubmitState {
  const { isSubmitting, isDirty, validationError, isRequiredFilled, isConfirmed } = options;

  return useMemo(() => {
    if (isSubmitting) {
      return { disabled: true, disabledTooltip: '' };
    }
    if (validationError) {
      return { disabled: true, disabledTooltip: validationError };
    }
    if (isDirty !== undefined && !isDirty) {
      return { disabled: true, disabledTooltip: 'No changes to save' };
    }
    if (isRequiredFilled !== undefined && !isRequiredFilled) {
      return {
        disabled: true,
        disabledTooltip: 'Please fill in required fields',
      };
    }
    if (isConfirmed !== undefined && !isConfirmed) {
      return {
        disabled: true,
        disabledTooltip: 'Please confirm to continue',
      };
    }
    return { disabled: false, disabledTooltip: '' };
  }, [isSubmitting, isDirty, validationError, isRequiredFilled, isConfirmed]);
}
